import { useState, useEffect, useCallback } from "react";
import {
  ArrowLeft, Building2, User, Mail, FileText, CreditCard,
  AlertTriangle, TrendingUp, DollarSign, ShieldCheck, Download,
  ChevronRight, Activity, Eye,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { KPICard } from "@/components/KPICard";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { getAdminDeals, getAdminDealDetail, fullReportPdfUrl } from "@/lib/api";

const fmt = (n) => "$" + Math.abs(Number(n || 0)).toLocaleString(undefined, { maximumFractionDigits: 0 });

const gradeColors = {
  A: "bg-[#2D4A3E] text-white",
  B: "bg-[#2D4A3E]/80 text-white",
  C: "bg-[#E3B505] text-[#1C1917]",
  D: "bg-[#E07A5F] text-white",
  F: "bg-[#E07A5F] text-white",
};

function DealDetail({ deal, onBack }) {
  const { analysis, owner, cash_flow, offers, comparison, recommendation, uploads, rating } = deal;
  const avg = cash_flow?.averages || {};
  const detected = cash_flow?.detected_loans || [];
  const flags = cash_flow?.red_flags || [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="deal-detail">
      <div className="flex items-center gap-3">
        <Button data-testid="deal-back-btn" variant="ghost" size="sm" onClick={onBack} className="hover:bg-[#F1EFEA]">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Deals
        </Button>
      </div>

      {/* Header */}
      <div className="bg-white border border-[#E7E5DF] rounded-md p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-['Playfair_Display'] font-semibold text-[#1C1917]">
                {analysis?.business_name || analysis?.name || "Untitled"}
              </h2>
              <span className={`text-lg font-bold px-3 py-1 rounded-full ${gradeColors[rating?.grade] || "bg-[#F1EFEA]"}`}>
                {rating?.grade}
              </span>
            </div>
            <p className="text-sm text-[#78716C] mt-1">{rating?.label} — {analysis?.name}</p>
          </div>
          <Button data-testid="download-full-report" onClick={() => window.open(fullReportPdfUrl(analysis?.id), "_blank")} className="bg-[#1A362D] hover:bg-[#264B40]">
            <Download className="w-4 h-4 mr-2" /> Full Report PDF
          </Button>
        </div>
        {owner && (
          <div className="flex items-center gap-4 mt-4 pt-4 border-t border-[#E7E5DF] text-sm">
            <span className="flex items-center gap-1 text-[#57534E]"><User className="w-4 h-4" /> {owner.name}</span>
            <span className="flex items-center gap-1 text-[#57534E]"><Mail className="w-4 h-4" /> {owner.email}</span>
          </div>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard title="Avg Monthly Revenue" value={fmt(avg.avg_monthly_revenue)} icon={DollarSign} />
        <KPICard title="Free Cash Flow" value={fmt(avg.free_cash_flow)} icon={TrendingUp}
          variant={avg.free_cash_flow > 5000 ? "success" : avg.free_cash_flow > 0 ? "warning" : "danger"} />
        <KPICard title="DSCR" value={avg.dscr >= 99 ? "N/A" : `${avg.dscr}x`} icon={ShieldCheck}
          variant={avg.dscr >= 1.5 ? "success" : avg.dscr >= 1.15 ? "warning" : "danger"} />
        <KPICard title="Red Flags" value={flags.length} icon={AlertTriangle}
          variant={flags.length === 0 ? "success" : "danger"} />
      </div>

      {/* Detected Existing Loans */}
      {detected.length > 0 && (
        <div className="bg-white border border-[#E7E5DF] rounded-md overflow-hidden">
          <div className="p-4 border-b border-[#E7E5DF]">
            <h3 className="text-lg font-['Playfair_Display'] font-semibold text-[#1C1917]">
              Detected Existing Loans ({detected.length})
            </h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F1EFEA]">
                <TableHead className="text-xs uppercase font-bold text-[#78716C]">Payee</TableHead>
                <TableHead className="text-xs uppercase font-bold text-[#78716C] text-right">Payment</TableHead>
                <TableHead className="text-xs uppercase font-bold text-[#78716C]">Frequency</TableHead>
                <TableHead className="text-xs uppercase font-bold text-[#78716C] text-right">Monthly Equiv.</TableHead>
                <TableHead className="text-xs uppercase font-bold text-[#78716C] text-right"># Payments</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {detected.map((l, i) => (
                <TableRow key={i} className="table-row-hover">
                  <TableCell className="py-3 text-sm text-[#1C1917] max-w-[200px] truncate">{l.payee}</TableCell>
                  <TableCell className="py-3 text-sm text-right font-['IBM_Plex_Mono'] text-[#E07A5F]">{fmt(l.payment_amount)}</TableCell>
                  <TableCell className="py-3"><Badge variant="secondary">{l.frequency}</Badge></TableCell>
                  <TableCell className="py-3 text-sm text-right font-['IBM_Plex_Mono'] font-semibold">{fmt(l.monthly_equivalent)}</TableCell>
                  <TableCell className="py-3 text-sm text-right font-['IBM_Plex_Mono']">{l.payment_count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Loan Comparison */}
      {comparison?.length > 0 && (
        <div className="bg-white border border-[#E7E5DF] rounded-md overflow-hidden">
          <div className="p-4 border-b border-[#E7E5DF]">
            <h3 className="text-lg font-['Playfair_Display'] font-semibold text-[#1C1917]">
              Loan Offers ({comparison.length})
            </h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F1EFEA]">
                <TableHead className="text-xs uppercase font-bold text-[#78716C]">#</TableHead>
                <TableHead className="text-xs uppercase font-bold text-[#78716C]">Lender</TableHead>
                <TableHead className="text-xs uppercase font-bold text-[#78716C] text-right">Amount</TableHead>
                <TableHead className="text-xs uppercase font-bold text-[#78716C] text-right">Mo. Payment</TableHead>
                <TableHead className="text-xs uppercase font-bold text-[#78716C] text-right">Total Cost</TableHead>
                <TableHead className="text-xs uppercase font-bold text-[#78716C] text-right">DSCR</TableHead>
                <TableHead className="text-xs uppercase font-bold text-[#78716C]">Verdict</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {comparison.map((o) => (
                <TableRow key={o.id} className="table-row-hover">
                  <TableCell className="py-3 font-['IBM_Plex_Mono'] font-bold text-[#1A362D]">#{o.rank}</TableCell>
                  <TableCell className="py-3 font-medium">{o.lender_name}</TableCell>
                  <TableCell className="py-3 text-right font-['IBM_Plex_Mono'] text-sm">{fmt(o.loan_amount)}</TableCell>
                  <TableCell className="py-3 text-right font-['IBM_Plex_Mono'] text-sm font-semibold">{fmt(o.monthly_debt_service)}</TableCell>
                  <TableCell className="py-3 text-right font-['IBM_Plex_Mono'] text-sm text-[#E07A5F]">{fmt(o.total_financing_cost)}</TableCell>
                  <TableCell className={`py-3 text-right font-['IBM_Plex_Mono'] font-semibold ${o.new_dscr >= 1.5 ? "text-[#2D4A3E]" : o.new_dscr >= 1.15 ? "text-[#E3B505]" : "text-[#E07A5F]"}`}>
                    {o.new_dscr >= 99 ? "N/A" : `${o.new_dscr}x`}
                  </TableCell>
                  <TableCell className="py-3">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${o.recommendation === "Good Fit" ? "bg-[#2D4A3E] text-white" : o.recommendation === "Borderline" ? "bg-[#E3B505] text-[#1C1917]" : "bg-[#E07A5F] text-white"}`}>
                      {o.recommendation}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Red Flags + Concerns */}
      {(flags.length > 0 || recommendation?.concerns?.length > 0) && (
        <div className="bg-white border border-[#E07A5F]/30 rounded-md p-6">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-[#E07A5F]" />
            <h3 className="text-lg font-['Playfair_Display'] font-semibold text-[#E07A5F]">Red Flags & Concerns</h3>
          </div>
          <ul className="space-y-1.5">
            {[...flags, ...(recommendation?.concerns || [])].map((c, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-[#57534E]">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#E07A5F] flex-shrink-0" />{c}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Uploads */}
      {uploads?.length > 0 && (
        <div className="bg-white border border-[#E7E5DF] rounded-md overflow-hidden">
          <div className="p-4 border-b border-[#E7E5DF]">
            <h3 className="text-lg font-['Playfair_Display'] font-semibold text-[#1C1917]">Uploaded Files ({uploads.length})</h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F1EFEA]">
                <TableHead className="text-xs uppercase font-bold text-[#78716C]">File</TableHead>
                <TableHead className="text-xs uppercase font-bold text-[#78716C]">Type</TableHead>
                <TableHead className="text-xs uppercase font-bold text-[#78716C] text-right">Transactions</TableHead>
                <TableHead className="text-xs uppercase font-bold text-[#78716C]">Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {uploads.map((u) => (
                <TableRow key={u.id}>
                  <TableCell className="py-2 text-sm">{u.filename}</TableCell>
                  <TableCell className="py-2"><Badge variant="secondary">{u.file_type?.toUpperCase()}</Badge></TableCell>
                  <TableCell className="py-2 text-right font-['IBM_Plex_Mono'] text-sm">{u.transaction_count}</TableCell>
                  <TableCell className="py-2 text-sm text-[#78716C]">{u.uploaded_at ? new Date(u.uploaded_at).toLocaleDateString() : ""}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}


export default function AdminCRM() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchDeals = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdminDeals();
      setDeals(res.data.deals || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchDeals(); }, [fetchDeals]);

  const handleViewDeal = async (analysisId) => {
    setDetailLoading(true);
    try {
      const res = await getAdminDealDetail(analysisId);
      setSelectedDeal(res.data);
    } catch (e) { console.error(e); }
    setDetailLoading(false);
  };

  if (detailLoading) {
    return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#1A362D] border-t-transparent rounded-full animate-spin" /></div>;
  }

  if (selectedDeal) {
    return <DealDetail deal={selectedDeal} onBack={() => setSelectedDeal(null)} />;
  }

  if (loading) {
    return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#1A362D] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-crm-section">
      <div>
        <h2 className="text-2xl sm:text-3xl font-['Playfair_Display'] font-semibold text-[#1C1917]">
          Deal Pipeline
        </h2>
        <p className="text-sm text-[#57534E] mt-1">{deals.length} deals across all users</p>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KPICard title="Total Deals" value={deals.length} icon={Building2} />
        <KPICard title="Strong (A/B)" value={deals.filter(d => ["A", "B"].includes(d.rating?.grade)).length} icon={ShieldCheck} variant="success" />
        <KPICard title="Fair (C)" value={deals.filter(d => d.rating?.grade === "C").length} icon={Activity} variant="warning" />
        <KPICard title="Weak (D/F)" value={deals.filter(d => ["D", "F"].includes(d.rating?.grade)).length} icon={AlertTriangle} variant="danger" />
      </div>

      {/* Deals Table */}
      <div className="bg-white border border-[#E7E5DF] rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F1EFEA]">
                <TableHead className="text-xs uppercase font-bold text-[#78716C]">Rating</TableHead>
                <TableHead className="text-xs uppercase font-bold text-[#78716C]">Business</TableHead>
                <TableHead className="text-xs uppercase font-bold text-[#78716C]">User</TableHead>
                <TableHead className="text-xs uppercase font-bold text-[#78716C] text-right">Revenue</TableHead>
                <TableHead className="text-xs uppercase font-bold text-[#78716C] text-right">Free CF</TableHead>
                <TableHead className="text-xs uppercase font-bold text-[#78716C] text-right">DSCR</TableHead>
                <TableHead className="text-xs uppercase font-bold text-[#78716C] text-right">Flags</TableHead>
                <TableHead className="text-xs uppercase font-bold text-[#78716C] text-right">Offers</TableHead>
                <TableHead className="text-xs uppercase font-bold text-[#78716C]">Date</TableHead>
                <TableHead className="text-xs uppercase font-bold text-[#78716C] w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {deals.map((d) => (
                <TableRow
                  key={d.analysis_id}
                  data-testid={`deal-row-${d.analysis_id}`}
                  className="table-row-hover cursor-pointer"
                  onClick={() => handleViewDeal(d.analysis_id)}
                >
                  <TableCell className="py-3">
                    <span className={`text-sm font-bold px-2.5 py-1 rounded-full ${gradeColors[d.rating?.grade] || "bg-[#F1EFEA]"}`}>
                      {d.rating?.grade}
                    </span>
                  </TableCell>
                  <TableCell className="py-3">
                    <p className="font-medium text-[#1C1917]">{d.business_name || d.analysis_name}</p>
                    <p className="text-xs text-[#78716C]">{d.transaction_count} tx / {d.upload_count} files</p>
                  </TableCell>
                  <TableCell className="py-3">
                    <p className="text-sm text-[#1C1917]">{d.owner_name}</p>
                    <p className="text-xs text-[#78716C]">{d.owner_email}</p>
                  </TableCell>
                  <TableCell className="py-3 text-right font-['IBM_Plex_Mono'] text-sm">{fmt(d.avg_monthly_revenue)}</TableCell>
                  <TableCell className={`py-3 text-right font-['IBM_Plex_Mono'] text-sm font-semibold ${d.free_cash_flow > 0 ? "text-[#2D4A3E]" : "text-[#E07A5F]"}`}>
                    {fmt(d.free_cash_flow)}
                  </TableCell>
                  <TableCell className={`py-3 text-right font-['IBM_Plex_Mono'] text-sm font-semibold ${d.dscr >= 1.5 ? "text-[#2D4A3E]" : d.dscr >= 1.15 ? "text-[#E3B505]" : "text-[#E07A5F]"}`}>
                    {d.dscr >= 99 ? "N/A" : `${d.dscr}x`}
                  </TableCell>
                  <TableCell className={`py-3 text-right text-sm font-semibold ${d.red_flag_count > 0 ? "text-[#E07A5F]" : "text-[#2D4A3E]"}`}>
                    {d.red_flag_count}
                  </TableCell>
                  <TableCell className="py-3 text-right font-['IBM_Plex_Mono'] text-sm">{d.offer_count}</TableCell>
                  <TableCell className="py-3 text-xs text-[#78716C] font-['IBM_Plex_Mono']">
                    {d.created_at ? new Date(d.created_at).toLocaleDateString() : ""}
                  </TableCell>
                  <TableCell className="py-3"><ChevronRight className="w-4 h-4 text-[#78716C]" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
